import React, { ReactNode } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildJoinView/GuildEmailInput'
import {
  GButtonMD,
  GHeadingLG,
  GHeadingSM,
  GResponsiveText,
  GStepValueLG,
  GStepValueXL,
  GTitleLG,
  GTitleSM,
  GTitleXL,
  GTitleXS,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { GiveawayHowToStep } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'

interface Props {
  steps: GiveawayHowToStep[]
  className?: string
  hasUser?: boolean
}

export const HowItWorks: React.FC<Props> = ({ steps, className, hasUser }) => {
  const { t } = useTranslate('tickets')
  const { scrollToProducts, theatricalSlug } = useGiveawayContext()

  const isNewDesign = [slugs.truthAndTreason, slugs.david, slugs.iWasAStranger].includes(theatricalSlug)

  return (
    <FlexColumn
      className={classNames('mx-auto max-w-[1080px] gap-4 pb-12 pt-[72px] md:pb-16 md:pt-[120px]', className)}
    >
      <GResponsiveText
        color="giveaway-text-primary-dark"
        className={'!font-bold'}
        MobileTextComponent={isNewDesign ? GHeadingSM : GTitleLG}
        DesktopTextComponent={isNewDesign ? GHeadingLG : GTitleXL}
      >
        {t('howItWorks', 'How it Works')}
      </GResponsiveText>

      {steps.map((step, index) => (
        <StepValue
          key={step.title}
          isNewDesign={isNewDesign}
          step={(index + 1).toString().padStart(2, '0')}
          theatricalSlug={theatricalSlug}
        >
          <strong>{step.title}</strong>: {step.description}
        </StepValue>
      ))}
      {theatricalSlug !== slugs.homesteadSeries && (
        <Button
          variant="core-gray-400"
          className={classNames(
            "mt-6 hidden !border-2 !border-giveaway-text-subtle-light px-[24px] pb-[15px] pt-[14px] !font-['bio-sans'] !text-giveaway-text-subtle-light md:block md:w-fit",
            theatricalSlug === slugs.sketch ? 'rounded-none' : 'rounded-xl',
          )}
          outline
          onClick={scrollToProducts}
        >
          <GButtonMD>{t('preOrderTicketsV2', 'Preorder Tickets')}</GButtonMD>
        </Button>
      )}

      {theatricalSlug === slugs.homesteadSeries && (
        <GuildEmailInput
          isCentered={false}
          isHero
          className="w-full md:!w-fit"
          inputContainerClassName="w-full !px-0 !max-w-none md:w-[300px] lg:w-[450px]"
          buttonTextOverride={hasUser ? t('watchToEnter', 'WATCH NOW TO ENTER') : t('enterToWin', 'ENTER TO WIN')}
          hasUser={hasUser}
          returnToUrl={`${paths.guild.join}/humvee`}
        />
      )}
    </FlexColumn>
  )
}

const StepValue = ({
  step,
  children,
  isNewDesign,
  theatricalSlug,
}: {
  step: string
  children: ReactNode
  isNewDesign: boolean
  theatricalSlug: string
}) => (
  <div className="mb-4 flex items-start justify-start gap-2">
    <div
      className={classNames(
        'flex rounded-md   px-2 py-0.5',
        isNewDesign ? 'bg-giveaway-primary-accent' : 'bg-giveaway-secondary',
        slugs.iWasAStranger === theatricalSlug && '!bg-giveaway-primary-subtle-dark',
        theatricalSlug === slugs.soloMio && '!bg-giveaway-primary-subtle',
      )}
    >
      <GResponsiveText
        color={slugs.soloMio === theatricalSlug ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
        MobileTextComponent={GStepValueLG}
        DesktopTextComponent={GStepValueXL}
      >
        {step}
      </GResponsiveText>
    </div>
    <GResponsiveText
      color="giveaway-text-primary-dark"
      className="!font-normal"
      MobileTextComponent={GTitleXS}
      DesktopTextComponent={GTitleSM}
    >
      {children}
    </GResponsiveText>
  </div>
)
